import { useEffect, useState } from "react";
import { fetchSensorConsentStatus } from "../../api/sensorConsentService";

/**
 * GMA-1585 — small read-only badge shown inside a sensor's staff pane that tells
 * admins + caregivers whether the client has signed the consent form for that
 * sensor (Pontosense or Apple Watch / HealthKit).
 *
 * Product rules:
 *   - When the sensor's admin toggle is OFF (`enabled === false`) the badge
 *     renders NOTHING. There is no reason to show — or collect — consent for a
 *     feature that is turned off for this client, so the consent line is hidden
 *     exactly like the rest of the sensor pane.
 *   - When ON, fetch the combined consent status and show a green "signed" or
 *     red "not signed" line for THIS sensorType.
 *   - On any fetch error, fail soft (render nothing) so the badge can never
 *     break the surrounding vitals / Pontosense pane.
 *
 * Consent is advisory (recorded for visibility/audit, GMA-1577) — it is not a
 * delivery gate; the admin toggle is the gate.
 */

/** Format an ISO timestamp as a short local date, or "" when absent/invalid. */
export const formatConsentDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export default function ConsentStatusBadge({ clientId, sensorType, enabled }) {
  const [state, setState] = useState(null);

  useEffect(() => {
    let active = true;
    // Clear first so a previous client's / sensor's consent can never linger on
    // screen while the new fetch is in flight (the badge renders nothing until
    // fresh data arrives or the sensor is disabled).
    setState(null);
    if (!enabled || !clientId) {
      return undefined;
    }
    fetchSensorConsentStatus(clientId)
      .then((status) => {
        if (active) setState(status?.[sensorType] || null);
      })
      .catch(() => {
        // Fail soft — never break the surrounding pane on a consent-read error.
        if (active) setState(null);
      });
    return () => {
      active = false;
    };
  }, [clientId, sensorType, enabled]);

  if (!enabled || !state) return null;

  const consented = state.consented === true;
  const when = formatConsentDate(state.recordedAt);

  return (
    <p
      data-testid={`consent-status-${sensorType}`}
      style={{
        fontSize: "0.85rem",
        margin: "0 0 12px 0",
        fontWeight: 600,
        color: consented ? "#2E7D32" : "#C62828",
      }}
    >
      {consented
        ? `✓ Client consent signed${when ? ` · ${when}` : ""}`
        : "⚠ Client has not signed the consent form"}
    </p>
  );
}
